import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'text';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Full width and a touch sized height, for the primary action on a form. */
  block?: boolean;
  icon?: ReactNode;
  /**
   * The command is on its way. A small spinner takes the label's place, the
   * button keeps its size so nothing around it moves, and it cannot be pressed
   * again until the answer comes. Request of 5 October 2026, section 5.
   */
  loading?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant = 'secondary',
  block = false,
  icon,
  loading = false,
  children,
  className,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  const classes = [
    styles.btn,
    styles[variant],
    block ? styles.block : '',
    loading ? styles.loading : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {/* Kept in place while loading, only hidden, so the button keeps its width. */}
      <span className={styles.label}>
        {icon}
        {children}
      </span>
      {loading && <span className={styles.spinner} aria-hidden="true" />}
    </button>
  );
}
